import { createContext, useContext } from "react";

import {
  ConnectionBaseFragment,
  DestinationPropertyFragment,
  ListObjectTypesQuery,
  OperationModeOptionFragment,
  OrchestrationSchedulerType,
  SourcePropertyFragment,
} from "@/apollo/types";

import { SourceModel } from "../steps/model-mapping/form";

type StepState<T> = {
  isValid: boolean;
  isDirty: boolean;
  value: T | null;
};

export type CustomDestinationPropertyOption = {
  value: string;
  label: string;
  meta?: undefined;
  isCustom: true;
};

export type FixedDestinationPropertyOption = {
  value: string;
  label: string;
  meta: DestinationPropertyFragment;
  isCustom?: undefined;
};

export type DestinationPropertyOption =
  | FixedDestinationPropertyOption
  | CustomDestinationPropertyOption;

export type PropertyMapping = {
  destinationProperty: DestinationPropertyOption;
  sourceProperty: SourcePropertyFragment;
};

export type PrimaryPropertyMapping = {
  destinationProperty: FixedDestinationPropertyOption;
  sourceProperty: SourcePropertyFragment;
};

export type ModelMappingState = {
  model: SourceModel;
  objectType: ListObjectTypesQuery["objectTypes"][0];
  objectTypeSettings?: Record<string, any>;
  mappings: PropertyMapping[];
  primaryMapping: PrimaryPropertyMapping | null;
  operation: OperationModeOptionFragment;
};

export type SyncSettingsState = {
  scheduleKey: string;
  schedulerType: OrchestrationSchedulerType;
  orchestrationWorkflowId?: string;
};

export type ConnectionSettingsState = {
  settings: Record<string, any>;
};

export type DestinationState = {
  integrationId?: string;
  connection?: ConnectionBaseFragment;
};

export type ReverseETLStepState = {
  destination: StepState<DestinationState>;
  connectionSettings: StepState<ConnectionSettingsState>;
  modelMapping: StepState<ModelMappingState>;
  syncSettings: StepState<SyncSettingsState>;
};

export const createInitialState = (values?: {
  integrationId?: string;
  connection?: ConnectionBaseFragment;
}): ReverseETLStepState => ({
  destination: {
    isValid: values?.connection ? true : false,
    isDirty: false,
    value: {
      integrationId: values?.integrationId,
      connection: values?.connection,
    },
  },
  connectionSettings: {
    isValid: false,
    isDirty: false,
    value: null,
  },
  modelMapping: {
    isValid: false,
    isDirty: false,
    value: null,
  },
  syncSettings: {
    isValid: false,
    isDirty: false,
    value: {
      scheduleKey: "0 0 * * *",
      schedulerType: OrchestrationSchedulerType.Local,
    },
  },
});

export const ReverseETLStateContext = createContext({
  state: createInitialState(),
  setState: (
    state:
      | ReverseETLStepState
      | ((state: ReverseETLStepState) => ReverseETLStepState),
  ) => {},
});

export function useReverseETLStateContext() {
  return useContext(ReverseETLStateContext);
}
